// =============================================================================
// Inventory Management — importing the operational tracker workbook.
//
// A dry run comes first and is the default, because the import is a mirror of a live spreadsheet and
// the only honest way to adopt one is to see what it would change before it changes it. The tie-out
// table beside the counts is the point: it compares what landed against what the workbook says, so a
// mapping that quietly dropped a sheet shows up as a number, not as a surprise three weeks later.
// =============================================================================

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Button } from '/imports/ui/Button';
import { LoadingFlex } from '/imports/ui/LoadingFlex';
import { toastOnError } from '/imports/ui/utils';
import { uploadFile } from '/imports/api/files';
import { FileMeta } from '/imports/types';
import { InventoryImportResult, inventoryImportTracker } from '/imports/api/inventory';
import { EmptyRow, InventoryPageStyled, count, useInventoryAccess } from '/imports/ui/InventoryShared';

const ImportPageStyled = styled(InventoryPageStyled)`
  .drop { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
  .ok { color: ${css.green}; }
  .bad { color: ${css.red5}; font-weight: 600; }
  .msg { font-size: 0.82rem; color: ${css.grey3}; }
  ul { margin: 0.25rem 0 0.75rem 1.1rem; padding: 0; }
`;

/** Loads the tracker workbook into the module, dry run first. */
export const InventoryImportPage = () => {
  const { canAccess, isSuperUser } = useInventoryAccess();
  const [file, setFile] = useState<FileMeta>();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InventoryImportResult>();

  const choose = toastOnError(async (chosen: File) => {
    setBusy(true);
    try {
      setFile(await uploadFile(chosen));
      setResult(undefined);
    }
    finally {
      setBusy(false);
    }
  });

  const run = toastOnError(async (dryRun: boolean) => {
    if (!file) { toast.info(strings.InventoryImportNoFile()); return; }
    setBusy(true);
    try {
      const outcome = await inventoryImportTracker.call({ file: { url: file.url, name: file.name }, dryRun });
      setResult(outcome);
      if (!dryRun) toast.success(strings.InventoryImportedFrom(file.name));
    }
    finally {
      setBusy(false);
    }
  });

  if (!canAccess || !isSuperUser) return <NotPermittedPage />;

  return (
    <ImportPageStyled className="page">
      <PageHeading left={<h1>{strings.InventoryImport()}</h1>} />
      <PageBody>
        <div className="drop">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) choose(f); }}
          />
          <Button className="secondary" disabled={!file || busy} onClick={() => run(true)}>{strings.DryRun()}</Button>
          <Button className="primary" disabled={!file || busy} onClick={() => run(false)}>{strings.InventoryApplyImport()}</Button>
          {busy && <LoadingFlex />}
        </div>
        <div className="note">{strings.InventoryImportDryRunNote()}</div>

        {result && <>
          <div className="msg">
            {result.fileName} · {result.dryRun ? strings.DryRun() : strings.InventoryApplyImport()} · {(result.millis / 1000).toFixed(1)}s
          </div>

          <div className="panels" style={{ marginTop: '0.75rem' }}>
            <div className="panel-card">
              <h2>{strings.InventoryImportResults()}</h2>
              <table className="inv">
                <thead>
                  <tr>
                    <th>{strings.Name()}</th>
                    <th className="num">{strings.Created()}</th>
                    <th className="num">{strings.Updated()}</th>
                    <th className="num">{strings.Skipped()}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.counts.map((c) => (
                    <tr key={c.collection}>
                      <td>{c.collection}</td>
                      <td className="num">{count(c.created)}</td>
                      <td className="num">{count(c.updated)}</td>
                      <td className="num">{count(c.unchanged)}</td>
                    </tr>
                  ))}
                  {!result.counts.length && <EmptyRow colSpan={4} />}
                </tbody>
              </table>
            </div>

            <div className="panel-card">
              <h2>{strings.InventoryTieOut()}</h2>
              <table className="inv">
                <thead>
                  <tr>
                    <th>{strings.Name()}</th>
                    <th className="num">{strings.Inventory()}</th>
                    <th className="num">{strings.InventoryImport()}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {result.tieOut.map((t) => (
                    <tr key={t.label}>
                      <td>{t.label}</td>
                      <td className="num">{typeof t.imported === 'number' ? t.imported.toLocaleString() : t.imported}</td>
                      <td className="num">{typeof t.workbook === 'number' ? t.workbook.toLocaleString() : t.workbook}</td>
                      <td className={t.ok ? 'ok' : 'bad'}>{t.ok ? '✓' : '✕'}</td>
                    </tr>
                  ))}
                  {!result.tieOut.length && <EmptyRow colSpan={4} />}
                </tbody>
              </table>
            </div>
          </div>

          {!!result.warnings.length && <>
            <h2 style={{ fontSize: '0.9rem', marginTop: '1rem' }}>{strings.InventoryWarnings()}</h2>
            <ul className="msg">{result.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
          </>}
          {!!result.errors.length && <>
            <h2 style={{ fontSize: '0.9rem', marginTop: '1rem' }} className="bad">{strings.InventoryErrors()}</h2>
            <ul className="msg">{result.errors.map((e) => <li key={e}>{e}</li>)}</ul>
          </>}
        </>}
      </PageBody>
    </ImportPageStyled>
  );
};
